import { GroupOutlined, NotificationsOutlined } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Chip,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { notificationGroupsByTenant } from '../../graphql/queries';

/**
 * A SaaS-styled optional notification group selector.
 * When a group is selected, notifications and emails will be sent to all group members
 * instead of just the current user.
 *
 * @param {Object} props
 * @param {Object|null} props.value - The currently selected notification group (or null for just me)
 * @param {Function} props.onChange - Callback with the selected group object or null
 */
export function NotificationGroupSelect({ value, onChange, placeholder = 'Just me' }) {
    const client = generateClient();
    const userContext = useContext(UserContext);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (userContext.tenantId) {
            loadGroups();
        }
    }, [userContext.tenantId]);

    async function loadGroups() {
        try {
            const result = await client.graphql({
                query: notificationGroupsByTenant,
                variables: { tenant: userContext.tenantId },
            });
            setGroups(result.data.notificationGroupsByTenant.items);
        } catch (err) {
            console.error('Error loading notification groups', err);
        }
        setLoading(false);
    }

    if (loading || groups.length === 0) return null;

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: value ? 'primary.main' : 'divider',
                backgroundColor: value ? 'primary.light' : 'transparent',
                transition: 'all 0.2s ease',
            }}
        >
            <NotificationsOutlined color={value ? 'primary' : 'action'} fontSize="small" />
            <Box display="flex" flexDirection="column" gap={0.5} sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    Notify group on completion (optional)
                </Typography>
                <Autocomplete
                    size="small"
                    options={groups}
                    value={value}
                    onChange={(e, newValue) => onChange(newValue)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={placeholder}
                            variant="standard"
                            sx={{ '& .MuiInput-underline:before': { borderBottom: 'none' } }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <GroupOutlined fontSize="small" color="primary" />
                                <Box>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.members.length} {option.members.length === 1 ? 'member' : 'members'}
                                    </Typography>
                                </Box>
                            </Box>
                        </li>
                    )}
                    PaperComponent={(props) => <Paper {...props} elevation={8} />}
                    noOptionsText="No notification groups created yet"
                    clearOnEscape
                />
            </Box>
            {value && (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {value.members.slice(0, 3).map(m => (
                        <Chip key={m.cognitoId} label={m.name} size="small" variant="outlined" color="primary" />
                    ))}
                    {value.members.length > 3 && (
                        <Chip label={`+${value.members.length - 3}`} size="small" color="primary" />
                    )}
                </Box>
            )}
        </Box>
    );
}
